import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateQmCostEntryInput,
  CreateQmProcessInput,
  CreateQmTimeEntryInput,
  ItemResponse,
  ListResponse,
  QmCostEntryResponse,
  QmProcessResponse,
  QmProcessStatus,
  QmProcessTemplateResponse,
  QmProcessType,
  QmTimeEntryResponse,
  UpdateQmProcessInput,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

const KEY = ['qm-processes'] as const;
const detailKey = (id: string) => [...KEY, 'detail', id] as const;

export interface QmProcessesQuery {
  q?: string;
  type?: QmProcessType | '';
  status?: QmProcessStatus | '';
  mine?: boolean;
  meeting?: boolean;
  projectId?: string;
}

export function useQmProcesses(query: QmProcessesQuery = {}) {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.type) params.set('type', query.type);
  if (query.status) params.set('status', query.status);
  if (query.mine) params.set('mine', 'true');
  if (query.meeting) params.set('meeting', 'true');
  if (query.projectId) params.set('projectId', query.projectId);
  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: [...KEY, query],
    queryFn: () => apiRequest<ListResponse<QmProcessResponse>>(`/v1/qm-processes${suffix}`),
  });
}

export function useQmProcess(id: string) {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: () => apiRequest<ItemResponse<QmProcessResponse>>(`/v1/qm-processes/${id}`),
    enabled: Boolean(id),
  });
}

export function useQmTemplates() {
  return useQuery({
    queryKey: ['qm-process-templates'],
    queryFn: () =>
      apiRequest<ListResponse<QmProcessTemplateResponse>>('/v1/qm-processes/templates'),
    staleTime: 5 * 60_000,
  });
}

export function useCreateQmProcess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQmProcessInput) =>
      apiRequest<ItemResponse<QmProcessResponse>>('/v1/qm-processes', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateQmProcess(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateQmProcessInput) =>
      apiRequest<ItemResponse<QmProcessResponse>>(`/v1/qm-processes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(detailKey(id), result);
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteQmProcess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/v1/qm-processes/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

// ── Kosten & Zeiten ─────────────────────────────────────────────────────────

const costsKey = (id: string) => [...KEY, 'detail', id, 'costs'] as const;
const timesKey = (id: string) => [...KEY, 'detail', id, 'times'] as const;

export function useQmCosts(id: string) {
  return useQuery({
    queryKey: costsKey(id),
    queryFn: () => apiRequest<ListResponse<QmCostEntryResponse>>(`/v1/qm-processes/${id}/costs`),
    enabled: Boolean(id),
  });
}

export function useAddQmCost(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQmCostEntryInput) =>
      apiRequest<ItemResponse<QmCostEntryResponse>>(`/v1/qm-processes/${id}/costs`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: costsKey(id) }),
  });
}

export function useDeleteQmCost(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (costId: string) =>
      apiRequest<void>(`/v1/qm-processes/${id}/costs/${costId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: costsKey(id) }),
  });
}

export function useQmTimes(id: string) {
  return useQuery({
    queryKey: timesKey(id),
    queryFn: () => apiRequest<ListResponse<QmTimeEntryResponse>>(`/v1/qm-processes/${id}/times`),
    enabled: Boolean(id),
  });
}

export function useAddQmTime(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQmTimeEntryInput) =>
      apiRequest<ItemResponse<QmTimeEntryResponse>>(`/v1/qm-processes/${id}/times`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: timesKey(id) }),
  });
}

export function useDeleteQmTime(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (timeId: string) =>
      apiRequest<void>(`/v1/qm-processes/${id}/times/${timeId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: timesKey(id) }),
  });
}
